import React, { useEffect, useState } from "react";
import { LocalUser, RemoteUser } from "agora-rtc-react";

import { BsFillMicMuteFill, BsFillMicFill } from "react-icons/bs";
import { IoVideocam, IoCameraReverse, IoVideocamOff } from "react-icons/io5";
import { FaPhoneAlt } from "react-icons/fa";
import { VscLoading } from "react-icons/vsc";
import { IoArrowBack } from "react-icons/io5";
// import { useAgoraVideoCall } from "@/hooks/use-agora-video-call";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Draggable from "react-draggable";
import SessionTimer from "@/components/SessionTimer";
// import { useAuth0 } from "@auth0/auth0-react";
import { useInstantCall } from "@/hooks/use-instant-call";
import { ImPhoneHangUp } from "react-icons/im";
import toast from "react-hot-toast";

function InstantVideoCallPage() {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const [hookError, setHookError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get navigation state
  const { channel, userSocketId } = location.state || {};
  
  // Call hook with channel from navigation state
  const hookResult = useInstantCall("video", channel);
  
  // Handle hook initialization errors
  useEffect(() => {
    if (!hookResult) {
      console.error("useInstantCall hook returned null");
      setHookError("Failed to initialize video call");
      toast.error("Failed to initialize video call. Redirecting...");
      setTimeout(() => navigate("/"), 1000);
      return;
    }
    
    // Check for missing navigation state
    const { channel, userSocketId } = hookResult;
    if (!channel || !userSocketId) {
      console.error("Missing navigation state - no channel or userSocketId");
      setHookError("Invalid call data");
      toast.error("Invalid call data. Redirecting...");
      setTimeout(() => navigate("/"), 1000);
      return;
    }
  }, [hookResult, navigate]);
  
  // Show loading state if hook is not ready
  if (hookError) {
    return (
      <div className="flex bg-black justify-center items-center w-full h-screen flex-col relative">
        <div className="text-white text-xl">Error: {hookError}</div>
      </div>
    );
  }
  
  const {
    localCameraTrack,
    localMicrophoneTrack,
    cameraOn,
    micOn,
    remoteUsers,
    setCamera,
    setMic,
    handleCameraSwitch,
    isConnected,
    setIsUserReady,
    // isPageReady,
    // isLoading,
    isUserReady,
    microphoneDevices,
    handleMicrophoneDeviceChange,
    cameraDevices,
    handleCameraDeviceChange,
    speakerDevices,
    handleSpeakerDeviceChange,
  } = hookResult || {};

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsUserReady(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [setIsUserReady]);

  const handleConnectionsClick = () => {
    if (isConnected) {
      setShowConfirmModal(true);
    } else {
      setIsUserReady(true);
    }
  };
  const handleEndCall = () => {
    setIsUserReady(false);
    setShowConfirmModal(false);
  };
  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const extractDeviceName = (label) => {
    return label.replace(/\s*\(.*?\)$/, "");
  };

  return (
    <div className="flex flex-col items-center h-screen">
      <div className="flex justify-between items-center border-b shadow-sm px-8 text-white w-full">
        <button
          onClick={() => {
            if (isConnected) {
              setShowConfirmModal(true);
            } else {
              navigate(-1);
            }
          }}
          className="hover:bg-orange-600 top-0 left-0 py-1 px-4 border text-lg bg-orange-400 text-white rounded-full shadow-md"
        >
          <IoArrowBack className="inline mb-1 mr-1" />
          Back
        </button>
        <img
          className="w-28 flex-shrink-0"
          src="/ApnaSanatan.svg"
          alt="apna-sanatan-logo"
        />
      </div>
      <div className="w-full flex flex-col lg:flex-row justify-center items-center  gap-10 relative p-5">
        <div className="flex justify-center items-center md:h-[70vh] h-96 md:max-w-[60vw] md:w-[75%] w-full relative">
          <div className="absolute top-0 right-4 z-30">
            <SessionTimer isConnected={isConnected} isUserReady={isUserReady} />
          </div>
          <LocalUser
            playAudio={false}
            audioTrack={localMicrophoneTrack}
            cameraOn={cameraOn}
            micOn={micOn}
            videoTrack={localCameraTrack}
            className="mt-2 rounded-xl"
            cover="unknown.jpg"
          >
            <span className="text-white absolute p-3 text-center w-full bottom-20 text-2xl">
              You
            </span>
          </LocalUser>
          <div className="flex justify-center items-center mt-3 absolute z-10 bottom-2">
            <div className="">
              <button
                className="text-3xl rounded-full p-3 text-white bg-green-600 mr-3"
                onClick={handleCameraSwitch}
              >
                <IoCameraReverse />
              </button>
              <button
                className={`text-3xl  rounded-full p-3 text-white ${
                  micOn ? "bg-green-600" : "bg-red-600"
                } mr-3`}
                onClick={() => setMic((prev) => !prev)}
              >
                {micOn ? <BsFillMicFill /> : <BsFillMicMuteFill />}
              </button>
              <button
                className={`text-3xl  rounded-full p-3 text-white ${
                  cameraOn ? "bg-green-600" : "bg-red-600"
                } mr-3`}
                onClick={() => setCamera((prev) => !prev)}
              >
                {cameraOn ? <IoVideocam /> : <IoVideocamOff />}
              </button>
            </div>
            <button
              className={`text-white rounded-full p-4 text-2xl ${
                !isConnected ? "bg-green-600" : "bg-red-600"
              }`}
              onClick={handleConnectionsClick}
            >
              {!isConnected ? <FaPhoneAlt /> : <ImPhoneHangUp />}
            </button>
          </div>
        </div>
        <div className="z-40 absolute flex gap-3 right-5 bottom-[-20px]">
          {isConnected &&
            remoteUsers.map((user, i) => (
              <Draggable key={i}>
                <div className="w-56 h-64 cursor-move" key={i}>
                  <RemoteUser
                    playAudio={false}
                    user={user}
                    className=" rounded-xl"
                    cover="unknown.jpg"
                  >
                    <span className="text-white p-3 text-2xl">{user.uid}</span>
                  </RemoteUser>
                </div>
              </Draggable>
            ))}
        </div>
        <div className="border-2 p-5 lg:w-[25%] w-full max-w-[96vw] text-center rounded-xl">
          <h1 className="text-center text-2xl font-bold mt-6">
            Lets begin your session
          </h1>
          <p className="text-xs text-gray-500 text-center">
            We are exited to guide you today.Let's make sure you are all set.
          </p>
          <div className="flex justify-center items-center mt-16">
            <div>
              <div className="border-2 p-3 border-orange-200 rounded-lg">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => toggleAccordion(1)}
                >
                  <h3 className="font-semibold text-gray-700">
                    Things to Keep in Mind Before You Start
                  </h3>
                  <span className="text-orange-500 text-lg">
                    {openIndex === 1 ? "-" : "+"}
                  </span>
                </div>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === 1 ? "max-h-screen" : "max-h-0"
                  }`}
                >
                  <div className="py-2 text-gray-600 text-sm">
                    Make sure you have all the necessary materials, and ensure a
                    stable internet connection before starting.
                  </div>
                </div>
              </div>
              <div className="border-2 p-3 mt-5 border-orange-200 rounded-lg">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => toggleAccordion(2)}
                >
                  <h3 className="font-semibold text-gray-700">
                    Avoid These for a Better Experience
                  </h3>
                  <span className="text-orange-500 text-lg">
                    {openIndex === 2 ? "-" : "+"}
                  </span>
                </div>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === 2 ? "max-h-screen" : "max-h-0"
                  }`}
                >
                  <div className="py-2 text-gray-600 text-sm">
                    Avoid distractions such as loud environments or multitasking
                    during the session.
                  </div>
                </div>
              </div>
              <div className="text-center text-xs text-gray-500 mt-4">
                Please review these guidelines carefully.
                <br /> Following the{" "}
                <span className="text-orange-500 cursor-pointer">
                  Do's and Don'ts
                </span>{" "}
                will help you get the most out of your session.
              </div>
            </div>
          </div>
          <button className="border tracking-wide bg-orange-600 text-white font-bold py-3 px-8 mt-10 text-center rounded-full text-xl">
            Join the Conversation
          </button>
          <p className="py-3 text-gray-600">00:00:09</p>
        </div>
      </div>
      <div className="flex flex-wrap text-sm justify-center items-center gap-2 p-5">
        {microphoneDevices && (
          <div className="border p-1 w-full sm:w-max rounded-lg shadow-md">
            <h3 className="text-center font-semibold">Microphone Devices</h3>
            <select onChange={handleMicrophoneDeviceChange}>
              {microphoneDevices.map((m, i) => (
                <option value={m.deviceId} key={i}>
                  {extractDeviceName(m.label)}
                </option>
              ))}
            </select>
          </div>
        )}
        {cameraDevices && (
          <div className="border p-1 w-full sm:w-max rounded-lg shadow-md">
            <h3 className="text-center font-semibold">Camera Devices</h3>
            <select onChange={handleCameraDeviceChange}>
              {cameraDevices.map((c, i) => (
                <option value={c.deviceId} key={i}>
                  {extractDeviceName(c.label)}
                </option>
              ))}
            </select>
          </div>
        )}
        {speakerDevices && (
          <div className="border p-1 w-full sm:w-max rounded-lg shadow-md">
            <h3 className="text-center font-semibold ">Speaker Devices</h3>
            <select onChange={handleSpeakerDeviceChange}>
              {speakerDevices.map((s, i) => (
                <option value={s.deviceId} key={i}>
                  {extractDeviceName(s.label)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={handleEndCall}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
}

export const ConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            End Session
          </h3>
          <p className="text-gray-600 mb-6">
            Do you want to complete the session?
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              End Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// if (!isPageReady || isLoading)
//   return (
//     <div className="flex bg-black justify-center items-center w-full h-screen flex-col relative">
//       <VscLoading size={50} color="#f16631" className="animate-spin" />
//     </div>
//   );

// return (
//   <div className="flex justify-center items-center w-full h-screen flex-col relative">
//     <div className="w-full flex-1 flex justify-center items-center relative">
//       <RemoteUser
//         playAudio={false}
//         user={remoteUsers[0]}
//         cover={() => (
//           <div className="w-full h-full justify-center items-center flex bg-[#eee]">
//             <div className="text-white flex justify-center items-center w-32 aspect-square bg-pink-500 rounded-full">
//               <p className="text-5xl">R</p>
//             </div>
//           </div>
//         )}
//       />
//       <div className="absolute h-72 sm:h-auto sm:min-w-72 aspect-[9/16] sm:w-1/4 sm:aspect-video bottom-3 right-3 z-10">
//         <LocalUser
//           playAudio={false}
//           audioTrack={localMicrophoneTrack}
//           cameraOn={cameraOn}
//           micOn={micOn}
//           videoTrack={localCameraTrack}
//           className="rounded-xl"
//           cover={() => (
//             <div className="w-full h-full justify-center items-center flex bg-[#eee] border border-black">
//               <div className="text-white flex justify-center items-center w-32 aspect-square bg-pink-500 rounded-full">
//                 <p className="text-5xl">L</p>
//               </div>
//             </div>
//           )}
//         />
//       </div>
//       <div className="flex justify-center items-center mt-3 absolute z-10 bottom-8">
//         <div className="">
//           <button
//             className="text-3xl rounded-full p-3 text-white bg-zinc-800 mr-3"
//             onClick={handleCameraSwitch}
//           >
//             <IoCameraReverse size={18} />
//           </button>
//           <button
//             className="text-3xl  rounded-full p-3 text-white bg-zinc-800 mr-3"
//             onClick={() => setMic((prev) => !prev)}
//           >
//             {micOn ? (
//               <BsFillMicFill size={18} />
//             ) : (
//               <BsFillMicMuteFill color="red" size={18} />
//             )}
//           </button>
//           <button
//             className="text-3xl rounded-full p-3 text-white bg-zinc-800 mr-3"
//             onClick={() => setCamera((prev) => !prev)}
//           >
//             {cameraOn ? (
//               <IoVideocam size={18} />
//             ) : (
//               <IoVideocamOff color="red" size={18} />
//             )}
//           </button>
//           {isConnected && (
//             <button
//               onClick={() => setIsUserReady(false)}
//               className="text-white rounded-full p-3 text-2xl bg-zinc-800"
//             >
//               <FaPhoneAlt color="red" size={18} />
//             </button>
//           )}
//         </div>
//       </div>
//     </div>
//   </div>
// );
// }

// {
//   <div className="justify-center items-center gap-5 p-2 flex-wrap hidden sm:flex">
//     {microphoneDevices && (
//       <div>
//         <h3 className="text-center font-semibold">Microphone Devices</h3>
//         <select className="truncate" onChange={handleMicrophoneDeviceChange}>
//           {microphoneDevices.map((m, i) => (
//             <option className="truncate" value={m.deviceId} key={i}>
//               {m.label}
//             </option>
//           ))}
//         </select>
//       </div>
//     )}
//     {cameraDevices && (
//       <div>
//         <h3 className="text-center font-semibold">Camera Devices</h3>
//         <select className="truncate" onChange={handleCameraDeviceChange}>
//           {cameraDevices.map((c, i) => (
//             <option className="truncate" value={c.deviceId} key={i}>
//               {c.label}
//             </option>
//           ))}
//         </select>
//       </div>
//     )}
//     {speakerDevices && (
//       <div>
//         <h3 className="text-center font-semibold ">Speaker Devices</h3>
//         <select className="truncate" onChange={handleSpeakerDeviceChange}>
//           {speakerDevices.map((s, i) => (
//             <option className="truncate" value={s.deviceId} key={i}>
//               {s.label}
//             </option>
//           ))}
//         </select>
//       </div>
//     )}
//   </div>;
// }

export default InstantVideoCallPage;
